import apiClient from "./apiClient";
import { API_BASE_URL, ENDPOINTS } from "./config";

// Types based on the admin API documentation
export interface DashboardStats {
  overview: {
    totalUsers: number;
    totalApplications: number;
    totalCourses: number;
    totalUniversities: number;
    recentApplications: number;
    recentUsers: number;
  };
  applicationsByStatus: {
    DRAFT: number;
    SUBMITTED: number;
    UNDER_REVIEW: number;
    ACCEPTED: number;
    REJECTED: number;
  };
  usersByRole: {
    STUDENT: number;
    UNIVERSITY_REP: number;
    AGENT: number;
  };
  documentsStatus: {
    verified: number;
    unverified: number;
  };
  profilesByStatus: {
    COMPLETED: number;
    PENDING: number;
    FAILED: number;
  };
  coursesByDegree: {
    BACHELOR: number;
    MASTER: number;
    PHD: number;
  };
  trends: {
    userRegistrationsByMonth: Array<{
      month: string;
      count: number;
    }>;
    applicationsByMonth: Array<{
      month: string;
      count: number;
    }>;
    applicationTrends: Array<{
      week: string;
      count: number;
      submitted: number;
      accepted: number;
    }>;
  };
  topPerformers: {
    universities: Array<{
      id: string;
      name: string;
      city: string;
      country: string;
      totalApplications: number;
      applicationCount: number;
      acceptanceRate: number;
    }>;
    courses: Array<{
      id: string;
      title: string;
      level: string;
      universityName: string;
      totalApplications: number;
      applicationCount: number;
      acceptanceRate: number;
    }>;
    agents: Array<{
      id: string;
      name: string;
      totalApplications: number;
      successRate: number;
    }>;
  };
}

export interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isEmailVerified: boolean;
  createdAt: string;
  lastLogin?: string;
  profile: {
    isCompleted: boolean;
    completionPercentage: number;
  };
  applications: {
    total: number;
    accepted: number;
    rejected: number;
    pending: number;
  };
  documents: {
    total: number;
    verified: number;
    pending: number;
  };
  wishlists: number;
  _count: {
    applications: number;
    wishlists: number;
  };
}

export interface AdminUserDetails {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isEmailVerified: boolean;
  createdAt: string;
  updatedAt: string;
  lastLogin?: string;
  address?: string;
  phone?: string;
  dateOfBirth?: string;
  nationality?: string;
  countryOfResidence?: string;
  profile: {
    isCompleted: boolean;
    completionPercentage: number;
  };
  applications: Array<{
    id: string;
    applicationNumber: string;
    status: string;
    createdAt: string;
    course: {
      id: string;
      title: string;
      university: {
        id: string;
        name: string;
        city: string;
        country: string;
      };
    };
  }>;
  documents: Array<{
    id: string;
    type: string;
    name: string;
    fileName: string;
    documentType: string;
    uploadedAt: string;
    verificationStatus: string;
    isVerified: boolean;
  }>;
  wishlists: Array<{
    id: string;
    createdAt: string;
    course: {
      id: string;
      title: string;
      university: {
        id: string;
        name: string;
      };
    };
    addedAt: string;
  }>;
}

export interface AdminApplication {
  id: string;
  applicationNumber: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  submittedAt?: string;
  givenNames: string;
  familyName: string;
  email: string;
  nationality: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  course: {
    id: string;
    title: string;
    university: {
      id: string;
      name: string;
      shortName: string;
      city: string;
      country: string;
    };
  };
  documents: Array<{
    id: string;
    type: string;
    fileName: string;
    verificationStatus: string;
    isVerified: boolean;
    documentType: string;
    uploadedAt: string;
  }>;
  notes?: string;
}

export interface UpdateApplicationStatusRequest {
  status: string;
  notes?: string;
  reviewNotes?: string;
}

export interface VerifyDocumentRequest {
  verificationStatus: string;
  notes?: string;
  isVerified?: boolean;
}

// University Management Types
export interface CreateUniversityRequest {
  name: string;
  shortName?: string;
  description?: string;
  website?: string;
  email?: string;
  phone?: string;
  address?: string;
  city: string;
  country: string;
  logoUrl?: string;
  bannerUrl?: string;
  isActive?: boolean;
}

export interface UpdateUniversityRequest {
  name?: string;
  shortName?: string;
  description?: string;
  website?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  country?: string;
  logoUrl?: string;
  bannerUrl?: string;
  isActive?: boolean;
}

export interface AdminUniversity {
  id: string;
  name: string;
  shortName?: string;
  description?: string;
  website?: string;
  email?: string;
  phone?: string;
  address?: string;
  city: string;
  country: string;
  logoUrl?: string;
  bannerUrl?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Course Management Types
export interface CreateCourseRequest {
  title: string;
  shortName?: string;
  description?: string;
  overview?: string;
  programStructure?: string;
  careerOpportunities?: string;
  degree: string;
  level: "BACHELOR" | "MASTER" | "DOCTORATE" | "CERTIFICATE" | "DIPLOMA";
  duration: number;
  studyMode: "FULL_TIME" | "PART_TIME" | "SANDWICH" | "DISTANCE_LEARNING";
  language?: string;
  courseCode?: string;
  entryRequirements?: string;
  languageRequirements?: string;
  otherRequirements?: string;
  tuitionFee?: string;
  applicationFee?: string;
  institutionHandlingFee?: string;
  tuitionFeeKRW?: string;
  tuitionFeeUSD?: string;
  onlineApplicationFee?: string;
  matriculationFee?: string;
  eligibility?: string;
  requirements?: string;
  documentRequirements?: string[];
  firstSemesterScholarship?: string;
  scholarshipFrom2ndSemester?: string;
  dormitoryFee?: string;
  bankStatementRequirement?: string;
  nextAvailableIntake?: string;
  universityId: string;
  isActive?: boolean;
}

export interface UpdateCourseRequest {
  title?: string;
  shortName?: string;
  description?: string;
  overview?: string;
  programStructure?: string;
  careerOpportunities?: string;
  degree?: string;
  level?: "BACHELOR" | "MASTER" | "DOCTORATE" | "CERTIFICATE" | "DIPLOMA";
  duration?: number;
  studyMode?: "FULL_TIME" | "PART_TIME" | "SANDWICH" | "DISTANCE_LEARNING";
  language?: string;
  courseCode?: string;
  entryRequirements?: string;
  languageRequirements?: string;
  otherRequirements?: string;
  tuitionFee?: string;
  applicationFee?: string;
  institutionHandlingFee?: string;
  tuitionFeeKRW?: string;
  tuitionFeeUSD?: string;
  onlineApplicationFee?: string;
  matriculationFee?: string;
  eligibility?: string;
  requirements?: string;
  documentRequirements?: string[];
  firstSemesterScholarship?: string;
  scholarshipFrom2ndSemester?: string;
  dormitoryFee?: string;
  bankStatementRequirement?: string;
  nextAvailableIntake?: string;
  universityId?: string;
  isActive?: boolean;
}

export interface AdminCourse {
  id: string;
  title: string;
  shortName?: string;
  description?: string;
  overview?: string;
  programStructure?: string;
  careerOpportunities?: string;
  degree: string;
  level: string;
  duration: number;
  studyMode: string;
  language?: string;
  courseCode?: string;
  entryRequirements?: string;
  languageRequirements?: string;
  otherRequirements?: string;
  tuitionFee?: string;
  applicationFee?: string;
  institutionHandlingFee?: string;
  tuitionFeeKRW?: string;
  tuitionFeeUSD?: string;
  onlineApplicationFee?: string;
  matriculationFee?: string;
  eligibility?: string;
  requirements?: string;
  documentRequirements?: string[];
  firstSemesterScholarship?: string;
  scholarshipFrom2ndSemester?: string;
  dormitoryFee?: string;
  bankStatementRequirement?: string;
  nextAvailableIntake?: string;
  universityId: string;
  university: AdminUniversity;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ApplicationFilters {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
  universityId?: string;
  courseId?: string;
}

export interface AdminApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

export interface AdminApplicationsResponse {
  applications: AdminApplication[];
  pagination: PaginationInfo;
}

export interface AdminUsersResponse {
  users: AdminUser[];
  pagination: PaginationInfo;
}

export interface AdminUniversitiesResponse {
  universities: AdminUniversity[];
  pagination: PaginationInfo;
}

export interface AdminCoursesResponse {
  courses: AdminCourse[];
  pagination: PaginationInfo;
}

export interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface PaginatedResponse<T> {
  success: boolean;
  message: string;
  data: {
    [key: string]: T[];
  } & {
    pagination: PaginationInfo;
  };
}

/**
 * AdminService class handles all admin-related API calls
 */
class AdminService {
  private handleApiResponse<T>(response: { data?: T; error?: { message: string; details?: any } }): T {
    if (response.error) {
      const details = response.error.details as any;
      const validationErrors: string[] | undefined = details?.errors;
      const message = (validationErrors?.length ? validationErrors[0] : null) || response.error.message;
      throw new Error(message);
    }
    if (!response.data) throw new Error('No data received from server');
    return response.data;
  }

  /**
   * Get dashboard statistics
   */
  async getDashboardStats(): Promise<AdminApiResponse<DashboardStats>> {
    const response = await apiClient.get<AdminApiResponse<DashboardStats>>(
      ENDPOINTS.ADMIN.STATS,
      undefined,
      true // withAuth = true
    );
    return this.handleApiResponse(response);
  }

  /**
   * Get all users with pagination and filtering
   */
  async getUsers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
    isEmailVerified?: boolean;
  }): Promise<AdminApiResponse<AdminUsersResponse>> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.search) queryParams.append("search", params.search);
    if (params?.role) queryParams.append("role", params.role);
    if (params?.isEmailVerified !== undefined) {
      queryParams.append("isEmailVerified", params.isEmailVerified.toString());
    }

    const url = `${ENDPOINTS.ADMIN.USERS}${queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;
    const response = await apiClient.get<AdminApiResponse<AdminUsersResponse>>(
      url,
      undefined,
      true
    ); // withAuth = true
    return this.handleApiResponse(response);
  }

  /**
   * Get user details by ID
   */
  async getUserDetails(
    id: string
  ): Promise<AdminApiResponse<AdminUserDetails>> {
    const response = await apiClient.get<AdminApiResponse<AdminUserDetails>>(
      ENDPOINTS.ADMIN.USER_DETAILS(id),
      undefined,
      true // withAuth = true
    );
    return this.handleApiResponse(response);
  }

  /**
   * Get all applications with pagination and filtering
   */
  async getApplications(params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
    universityId?: string;
    courseId?: string;
  }): Promise<PaginatedResponse<AdminApplication>> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.status) queryParams.append("status", params.status);
    if (params?.search) queryParams.append("search", params.search);
    if (params?.universityId)
      queryParams.append("universityId", params.universityId);
    if (params?.courseId) queryParams.append("courseId", params.courseId);

    const url = `${ENDPOINTS.ADMIN.APPLICATIONS}${queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;
    const response = await apiClient.get<PaginatedResponse<AdminApplication>>(
      url,
      undefined,
      true
    ); // withAuth = true
    return this.handleApiResponse(response);
  }

  /**
   * Delete an application and all associated data
   */
  async deleteApplication(id: string): Promise<AdminApiResponse<any>> {
    const response = await apiClient.delete<AdminApiResponse<any>>(
      ENDPOINTS.ADMIN.APPLICATION_DELETE(id),
      true
    );
    return this.handleApiResponse(response);
  }

  /**
   * Update application status
   */
  async updateApplicationStatus(
    id: string,
    request: UpdateApplicationStatusRequest
  ): Promise<AdminApiResponse<AdminApplication>> {
    const response = await apiClient.put<AdminApiResponse<AdminApplication>>(
      ENDPOINTS.ADMIN.APPLICATION_STATUS(id),
      request,
      true // withAuth = true
    );
    return this.handleApiResponse(response);
  }

  /**
   * Verify/reject a document
   */
  async verifyDocument(
    id: string,
    request: VerifyDocumentRequest
  ): Promise<AdminApiResponse<any>> {
    const response = await apiClient.put<AdminApiResponse<any>>(
      ENDPOINTS.ADMIN.VERIFY_DOCUMENT(id),
      request,
      true // withAuth = true
    );
    return this.handleApiResponse(response);
  }

  // University Management
  /**
   * Create a new university
   */
  async createUniversity(
    request: CreateUniversityRequest
  ): Promise<AdminApiResponse<AdminUniversity>> {
    const response = await apiClient.post<AdminApiResponse<AdminUniversity>>(
      ENDPOINTS.UNIVERSITIES.CREATE,
      request,
      true // withAuth = true
    );
    return this.handleApiResponse(response);
  }

  /**
   * Update an existing university
   */
  async updateUniversity(
    id: string,
    request: UpdateUniversityRequest
  ): Promise<AdminApiResponse<AdminUniversity>> {
    const response = await apiClient.put<AdminApiResponse<AdminUniversity>>(
      ENDPOINTS.UNIVERSITIES.UPDATE(id),
      request,
      true // withAuth = true
    );
    return this.handleApiResponse(response);
  }

  /**
   * Delete a university (soft delete)
   */
  async deleteUniversity(
    id: string
  ): Promise<AdminApiResponse<AdminUniversity>> {
    const response = await apiClient.delete<AdminApiResponse<AdminUniversity>>(
      ENDPOINTS.UNIVERSITIES.DELETE(id),
      true // withAuth = true
    );
    return this.handleApiResponse(response);
  }

  /**
   * Upload logo or banner image for a university
   */
  async uploadUniversityImage(
    id: string,
    file: File,
    imageType: 'logo' | 'banner'
  ): Promise<AdminApiResponse<AdminUniversity>> {
    const token = localStorage.getItem('authToken');
    const fd = new FormData();
    fd.append('image', file);
    fd.append('imageType', imageType);
    const response = await fetch(
      `${API_BASE_URL}${ENDPOINTS.ADMIN.UPLOAD_UNIVERSITY_IMAGE(id)}`,
      {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error?.message || 'Failed to upload image');
    }
    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Failed to upload image');
    return data;
  }

  /**
   * Get all universities
   */
  async getUniversities(params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
  }): Promise<AdminApiResponse<AdminUniversitiesResponse>> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.search) queryParams.append("search", params.search);
    if (params?.isActive !== undefined) {
      queryParams.append("isActive", params.isActive.toString());
    }

    const url = `${ENDPOINTS.UNIVERSITIES.GET_ALL}${queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;
    const response = await apiClient.get<
      AdminApiResponse<AdminUniversitiesResponse>
    >(url, undefined, true); // withAuth = true
    return this.handleApiResponse(response);
  }

  // Course Management
  /**
   * Create a new course
   */
  async createCourse(
    request: CreateCourseRequest
  ): Promise<AdminApiResponse<AdminCourse>> {
    const response = await apiClient.post<AdminApiResponse<AdminCourse>>(
      ENDPOINTS.COURSES.CREATE,
      request,
      true // withAuth = true
    );
    return this.handleApiResponse(response);
  }

  /**
   * Update an existing course
   */
  async updateCourse(
    id: string,
    request: UpdateCourseRequest
  ): Promise<AdminApiResponse<AdminCourse>> {
    const response = await apiClient.put<AdminApiResponse<AdminCourse>>(
      ENDPOINTS.COURSES.UPDATE(id),
      request,
      true // withAuth = true
    );
    return this.handleApiResponse(response);
  }

  /**
   * Delete a course (soft delete)
   */
  async deleteCourse(id: string): Promise<AdminApiResponse<AdminCourse>> {
    const response = await apiClient.delete<AdminApiResponse<AdminCourse>>(
      ENDPOINTS.COURSES.DELETE(id),
      true // withAuth = true
    );
    return this.handleApiResponse(response);
  }

  /**
   * Get all courses for admin
   */
  async getCourses(params?: {
    page?: number;
    limit?: number;
    search?: string;
    level?: string;
    universityId?: string;
    isActive?: boolean;
  }): Promise<AdminApiResponse<AdminCoursesResponse>> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.search) queryParams.append("search", params.search);
    if (params?.level) queryParams.append("level", params.level);
    if (params?.universityId)
      queryParams.append("universityId", params.universityId);
    if (params?.isActive !== undefined) {
      queryParams.append("isActive", params.isActive.toString());
    }

    const url = `${ENDPOINTS.COURSES.GET_ALL_COURSES}${queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;
    const response = await apiClient.get<
      AdminApiResponse<AdminCoursesResponse>
    >(url, undefined, true); // withAuth = true
    return this.handleApiResponse(response);
  }
}

// Create and export a singleton instance
const adminService = new AdminService();
export default adminService;
